import React, { useCallback, useEffect, useMemo, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import { Button, Form, Spinner, Alert, Modal, Table } from "react-bootstrap";

/** Повне перенаправлення на OAuth має йти на бекенд */
const BACKEND_ORIGIN =
    process.env.REACT_APP_API_ORIGIN ||
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5001");

/** Домен магазину за замовчуванням у полі підключення */
const DEFAULT_SHOPIFY_SHOP_DOMAIN = "843ff4-3.myshopify.com";

function shopifyOAuthUrl(shop) {
    const base = (BACKEND_ORIGIN || "").replace(/\/$/, "");
    const path = `/api/shopify/oauth?shop=${encodeURIComponent(shop)}`;
    return base ? `${base}${path}` : path;
}

function shopifyStatusLabel(status) {
    if (status == null || status === "") return "—";
    const u = String(status).toUpperCase();
    if (u === "ACTIVE") return "Active";
    if (u === "DRAFT") return "Чернетка";
    if (u === "ARCHIVED") return "Архів";
    if (u === "UNLISTED") return "Прихований";
    return u;
}

function shopifyErrorHint(error) {
    const msg = String(error || "").toLowerCase();
    if (msg.includes("fetch failed")) {
        return "Live-запит до Shopify не пройшов. Якщо магазин щойно перепідключили, натисніть «Оновити з KeyCRM/Shopify».";
    }
    return "Перевірте токен (перепідключіть магазин).";
}

/** Ефективна ціна рядка: Shopify, інакше KeyCRM */
function rowDisplayPriceUah(row) {
    if (row.shopifyPrice != null && Number.isFinite(Number(row.shopifyPrice))) {
        return Number(row.shopifyPrice);
    }
    if (row.crmPrice != null && Number.isFinite(Number(row.crmPrice))) {
        return Number(row.crmPrice);
    }
    return null;
}

/** Шапка акордеона (справа): одна сума або діапазон */
function formatGroupHeaderPrice(items) {
    const nums = items.map(rowDisplayPriceUah).filter((n) => n != null);
    if (!nums.length) return "—";
    const mn = Math.min(...nums);
    const mx = Math.max(...nums);
    if (mn === mx) return `${mn} грн`;
    return `(${mn} – ${mx} грн)`;
}

function groupRowsByProduct(rows) {
    const map = new Map();
    for (const r of rows) {
        const key = r.productId ?? r.productName;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
    }
    const groups = [...map.entries()].map(([key, items]) => ({
        key,
        name: items[0]?.productName || String(key),
        items: items.sort((a, b) =>
            (a.propsLabel || "").localeCompare(b.propsLabel || "", "uk")
        ),
    }));
    groups.sort((a, b) => a.name.localeCompare(b.name, "uk"));
    return groups;
}

function placeholderNewPrice(row) {
    if (row.shopifyPrice != null && Number.isFinite(Number(row.shopifyPrice))) {
        return String(row.shopifyPrice);
    }
    if (row.crmPrice != null) return String(row.crmPrice);
    return "";
}

const EDITOR_ROLES = new Set(["admin", "director", "finance"]);

function isPriceEditor(user) {
    return user?.role && EDITOR_ROLES.has(user.role);
}

export const PriceSheet = ({ user }) => {
    const [loading, setLoading] = useState(true);
    const [rebuilding, setRebuilding] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [onlyMismatch, setOnlyMismatch] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ACTIVE");
    const [shopInput, setShopInput] = useState(DEFAULT_SHOPIFY_SHOP_DOMAIN);
    const [rowDrafts, setRowDrafts] = useState({});
    const [bulkDrafts, setBulkDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [savingGroupKey, setSavingGroupKey] = useState(null);
    const [showChangelog, setShowChangelog] = useState(false);
    const [changelogLoading, setChangelogLoading] = useState(false);
    const [changelogItems, setChangelogItems] = useState([]);

    const canEdit = isPriceEditor(user);
    /** Менеджерський перегляд: лише заголовок «Прайс», акордеон, спрощена таблиця */
    const isSimplifiedView = !canEdit;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch("/api/prices/sheet", { credentials: "include" });
            if (!res.ok) throw new Error("Не вдалося завантажити прайс");
            const data = await res.json();
            setPayload(data);
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, []);

    const rebuildFromSources = useCallback(async () => {
        setRebuilding(true);
        setError(null);
        try {
            const res = await fetch("/api/prices/rebuild", {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `Помилка ${res.status}`);
            setPayload(data);
        } catch (e) {
            setError(e.message || "Помилка оновлення");
        } finally {
            setRebuilding(false);
            setLoading(false);
        }
    }, []);

    const openChangelog = async () => {
        setShowChangelog(true);
        setChangelogLoading(true);
        try {
            const res = await fetch("/api/prices/changelog?limit=60", { credentials: "include" });
            const data = await res.json().catch(() => ({}));
            if (res.ok) setChangelogItems(data.items || []);
            else setChangelogItems([]);
        } finally {
            setChangelogLoading(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const shopifyConnected = params.get("shopify_connected");
        const shopifyError = params.get("shopify_error");
        if (shopifyError) {
            const msg = params.get("msg");
            alert(msg ? decodeURIComponent(msg) : "Помилка підключення Shopify");
        }
        if (shopifyConnected || shopifyError) {
            window.history.replaceState({}, "", "/price");
        }
        if (shopifyConnected && canEdit) {
            rebuildFromSources();
            return;
        }
        load();
    }, [canEdit, load, rebuildFromSources]);

    const statusFilteredRows = useMemo(() => {
        if (!payload?.rows) return [];
        const sf = isSimplifiedView ? "ALL" : statusFilter;
        return payload.rows.filter((r) => {
            const s = r.shopifyStatus ? String(r.shopifyStatus).toUpperCase() : null;
            if (sf === "ALL") return true;
            if (sf === "ACTIVE") {
                if (s == null) return true;
                return s === "ACTIVE";
            }
            if (sf === "DRAFT") return s === "DRAFT";
            if (sf === "ARCHIVED") return s === "ARCHIVED";
            return true;
        });
    }, [payload, statusFilter, isSimplifiedView]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return statusFilteredRows.filter((r) => {
            if (onlyMismatch && r.priceMatch !== false) return false;
            if (!q) return true;
            const hay = `${r.productName} ${r.sku} ${r.propsLabel}`.toLowerCase();
            return hay.includes(q);
        });
    }, [statusFilteredRows, onlyMismatch, search]);

    const groups = useMemo(() => groupRowsByProduct(filteredRows), [filteredRows]);

    const startShopifyOAuth = () => {
        const shop = (shopInput || "").trim().toLowerCase();
        if (!shop) {
            alert(`Введіть домен магазину, наприклад ${DEFAULT_SHOPIFY_SHOP_DOMAIN}`);
            return;
        }
        window.location.assign(shopifyOAuthUrl(shop));
    };

    const reconnectShopify = () => {
        const s = window.prompt(
            "Домен магазину (*.myshopify.com)",
            payload?.shop || DEFAULT_SHOPIFY_SHOP_DOMAIN
        );
        if (!s) return;
        const shop = s.trim().toLowerCase();
        if (!shop) return;
        window.location.assign(shopifyOAuthUrl(shop));
    };

    const applyPrice = async (row) => {
        const draft = rowDrafts[row.offerId];
        const priceNum = draft != null && draft !== "" ? Number(String(draft).replace(",", ".")) : NaN;
        if (!Number.isFinite(priceNum) || priceNum < 0) {
            alert("Вкажіть коректну ціну");
            return;
        }
        setSavingId(row.offerId);
        try {
            const res = await fetch("/api/prices/update", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    offerId: row.offerId,
                    shopifyVariantId: row.shopifyVariantId,
                    price: priceNum,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || `Помилка ${res.status}`);
            }
            if (data.warning) {
                alert(data.error || "Часткове оновлення");
            }
            await load();
            setRowDrafts((prev) => {
                const next = { ...prev };
                delete next[row.offerId];
                return next;
            });
        } catch (e) {
            alert(e.message || "Не вдалося оновити");
        } finally {
            setSavingId(null);
        }
    };

    const applyBulkForGroup = async (g) => {
        const raw = bulkDrafts[g.key];
        const priceNum = raw != null && raw !== "" ? Number(String(raw).replace(",", ".")) : NaN;
        if (!Number.isFinite(priceNum) || priceNum < 0) {
            alert("Вкажіть коректну ціну для всіх варіантів");
            return;
        }
        const items = g.items.map((r) => ({
            offerId: r.offerId,
            shopifyVariantId: r.shopifyVariantId,
        }));
        setSavingGroupKey(g.key);
        try {
            const res = await fetch("/api/prices/bulk-update", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items, price: priceNum }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || `Помилка ${res.status}`);
            }
            if (data.errors?.length) {
                alert(
                    `Оновлено: ${data.updated}. Помилок: ${data.errors.length}. Перший: ${data.errors[0].error}`
                );
            }
            await load();
            setBulkDrafts((prev) => {
                const next = { ...prev };
                delete next[g.key];
                return next;
            });
            setRowDrafts((prev) => {
                const next = { ...prev };
                for (const r of g.items) delete next[r.offerId];
                return next;
            });
        } catch (e) {
            alert(e.message || "Не вдалося оновити");
        } finally {
            setSavingGroupKey(null);
        }
    };

    const formatSnapshotAt = (d) => {
        if (!d) return "";
        try {
            return new Date(d).toLocaleString("uk-UA", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return "";
        }
    };

    if (loading && !payload) {
        return (
            <div className="container-fluid px-2 px-sm-3 mt-3 d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span>Завантаження прайсу…</span>
            </div>
        );
    }

    return (
        <div className="container-fluid px-2 px-sm-3 mt-3 mb-5 price-sheet-page">
            <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-between align-items-stretch align-items-sm-center gap-2 mb-3">
                <h3 className="mb-0 text-break">Прайс</h3>
                {!isSimplifiedView ? (
                    <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
                        {canEdit && (
                            <>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="text-nowrap"
                                    onClick={rebuildFromSources}
                                    disabled={rebuilding || loading}
                                >
                                    {rebuilding ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Оновлення…
                                        </>
                                    ) : (
                                        "Оновити з KeyCRM/Shopify"
                                    )}
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={openChangelog}>
                                    Журнал змін
                                </Button>
                            </>
                        )}
                        <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
                            Перечитати кеш
                        </Button>
                    </div>
                ) : null}
            </div>

            {!isSimplifiedView && payload?.fromCache && payload.snapshotAt && (
                <Alert variant="secondary" className="py-2 small mb-3">
                    Оновлено <strong>{formatSnapshotAt(payload.snapshotAt)}</strong>
                    {payload.snapshotBy ? (
                        <>
                            {" "}
                            (<strong>{payload.snapshotBy}</strong>)
                        </>
                    ) : null}
                    {canEdit ? (
                        <span className="ms-1">
                            — натисніть «Оновити з KeyCRM/Shopify» для актуальних даних з систем.
                        </span>
                    ) : null}
                </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {payload?.snapshotEmpty && (
                <Alert variant="warning" className="d-flex flex-column gap-2">
                    <div>{payload.message || "Немає збереженого прайсу."}</div>
                    {canEdit && (
                        <Button size="sm" variant="primary" onClick={rebuildFromSources} disabled={rebuilding}>
                            Зберегти перший знімок з KeyCRM/Shopify
                        </Button>
                    )}
                </Alert>
            )}

            {payload?.shopifyError && (
                <Alert variant="warning">
                    Shopify: {payload.shopifyError}. {shopifyErrorHint(payload.shopifyError)}
                </Alert>
            )}

            {canEdit && !payload?.connected ? (
                <Alert variant="info" className="d-flex flex-column gap-2">
                    <div>Shopify ще не підключено. Після входу вкажіть домен магазину і натисніть «Підключити».</div>
                    <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch price-sheet-shop-connect">
                        <Form.Control
                            className="flex-grow-1"
                            placeholder={DEFAULT_SHOPIFY_SHOP_DOMAIN}
                            value={shopInput}
                            onChange={(e) => setShopInput(e.target.value)}
                        />
                        <Button variant="primary" className="flex-shrink-0" onClick={startShopifyOAuth}>
                            Підключити Shopify
                        </Button>
                    </div>
                </Alert>
            ) : null}

            {canEdit && payload?.connected ? (
                <Alert variant="success" className="py-2 d-flex flex-wrap align-items-center gap-2 column-gap-2">
                    <span className="text-break">
                        Підключено: <strong>{payload.shop}</strong>
                    </span>
                    <Button variant="link" size="sm" className="p-0 align-baseline text-nowrap" onClick={reconnectShopify}>
                        Перепідключити
                    </Button>
                </Alert>
            ) : null}

            {!isSimplifiedView ? (
                <div className="d-flex flex-column flex-md-row flex-wrap gap-2 gap-md-3 align-items-stretch align-items-md-center mb-3">
                    <Form.Group className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 mb-0">
                        <Form.Label className="mb-0 small text-nowrap">Статус у Shopify</Form.Label>
                        <Form.Select
                            size="sm"
                            className="price-sheet-status-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="ACTIVE">Active (за замовч.)</option>
                            <option value="DRAFT">Чернетка</option>
                            <option value="ARCHIVED">Архів</option>
                            <option value="ALL">Усі</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="only-mismatch"
                        className="d-flex align-items-center"
                        label="Лише розбіжності"
                        checked={onlyMismatch}
                        onChange={(e) => setOnlyMismatch(e.target.checked)}
                    />
                    <Form.Control
                        type="search"
                        className="flex-grow-1"
                        placeholder="Пошук за назвою, SKU, варіантом…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            ) : (
                <Form.Control
                    type="search"
                    className="mb-3"
                    placeholder="Пошук за назвою, SKU, варіантом…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            )}

            {!groups.length && !payload?.snapshotEmpty ? (
                <p className="text-muted">Немає рядків за поточними фільтрами.</p>
            ) : !payload?.snapshotEmpty ? (
                <Accordion alwaysOpen className="lava-accordion price-sheet-accordion">
                    {groups.map((g, idx) => (
                        <Accordion.Item eventKey={String(idx)} key={g.key}>
                            <Accordion.Header>
                                <span className="price-sheet-acc-head-inner">
                                    <span className="price-sheet-acc-title-block">
                                        <span className="price-sheet-acc-product">{g.name}</span>
                                        <span className="price-sheet-acc-count">({g.items.length})</span>
                                    </span>
                                    <span className="price-sheet-acc-price">{formatGroupHeaderPrice(g.items)}</span>
                                </span>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                                {canEdit ? (
                                    <div className="d-flex flex-column flex-sm-row flex-wrap align-items-stretch align-items-sm-center gap-2 px-2 py-2 border-bottom bg-body-secondary">
                                        <span className="small fw-semibold">Ціна для всіх варіантів:</span>
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                inputMode="decimal"
                                                style={{ maxWidth: 140, minWidth: 96 }}
                                                placeholder="Нова ціна"
                                                value={bulkDrafts[g.key] ?? ""}
                                                onChange={(e) =>
                                                    setBulkDrafts((prev) => ({
                                                        ...prev,
                                                        [g.key]: e.target.value,
                                                    }))
                                                }
                                            />
                                            <Button
                                                size="sm"
                                                variant="success"
                                                disabled={savingGroupKey === g.key}
                                                onClick={() => applyBulkForGroup(g)}
                                            >
                                                {savingGroupKey === g.key ? "…" : "Застосувати всі"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : null}
                                <div className="table-responsive price-sheet-table-scroll">
                                    <table
                                        className={`table table-sm table-bordered mb-0 price-sheet-table${
                                            isSimplifiedView ? " price-sheet-table--simple" : ""
                                        }`}
                                    >
                                        <thead className="table-light">
                                            {isSimplifiedView ? (
                                                <tr>
                                                    <th>Варіант</th>
                                                    <th>SKU</th>
                                                    <th>Вартість</th>
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <th>Варіант</th>
                                                    <th>SKU</th>
                                                    <th>KeyCRM</th>
                                                    <th>Shopify</th>
                                                    <th>Статус Shopify</th>
                                                    <th>Збіг</th>
                                                    <th>Нова ціна</th>
                                                    <th className="text-nowrap">Дія</th>
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {g.items.map((row) => {
                                                const matchIcon =
                                                    row.priceMatch === true
                                                        ? "✓"
                                                        : row.priceMatch === false
                                                          ? "✗"
                                                          : "—";
                                                const rowClass =
                                                    row.priceMatch === false
                                                        ? "table-warning"
                                                        : row.shopifyMissing
                                                          ? "table-secondary"
                                                          : "";
                                                const effPriceUah = rowDisplayPriceUah(row);
                                                if (isSimplifiedView) {
                                                    return (
                                                        <tr key={row.offerId} className={rowClass}>
                                                            <td>{row.propsLabel || "—"}</td>
                                                            <td>
                                                                <code>{row.sku || "—"}</code>
                                                            </td>
                                                            <td>
                                                                {effPriceUah != null ? (
                                                                    <>{effPriceUah} грн</>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                return (
                                                    <tr key={row.offerId} className={rowClass}>
                                                        <td>{row.propsLabel || "—"}</td>
                                                        <td>
                                                            <code>{row.sku || "—"}</code>
                                                        </td>
                                                        <td>
                                                            {row.crmMissing ? (
                                                                <span className="text-muted">немає</span>
                                                            ) : (
                                                                row.crmPrice
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.shopifyPrice == null ? (
                                                                <span className="text-muted">
                                                                    {row.shopifyMissing ? "немає в Shopify" : "—"}
                                                                </span>
                                                            ) : (
                                                                row.shopifyPrice
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.shopifyMissing ? (
                                                                <span className="text-muted">—</span>
                                                            ) : (
                                                                shopifyStatusLabel(row.shopifyStatus)
                                                            )}
                                                        </td>
                                                        <td>{matchIcon}</td>
                                                        <td style={{ minWidth: 110 }}>
                                                            <Form.Control
                                                                size="sm"
                                                                type="text"
                                                                inputMode="decimal"
                                                                placeholder={placeholderNewPrice(row)}
                                                                value={rowDrafts[row.offerId] ?? ""}
                                                                onChange={(e) =>
                                                                    setRowDrafts((prev) => ({
                                                                        ...prev,
                                                                        [row.offerId]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                disabled={savingId === row.offerId}
                                                                onClick={() => applyPrice(row)}
                                                            >
                                                                {savingId === row.offerId ? "…" : "Застосувати"}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            ) : null}

            <Modal
                show={showChangelog}
                onHide={() => setShowChangelog(false)}
                size="xl"
                fullscreen="sm-down"
                centered
                scrollable
                className="price-sheet-changelog-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Журнал змін цін</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {changelogLoading ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <div className="table-responsive price-sheet-table-scroll">
                        <Table striped size="sm" className="mb-0 price-sheet-changelog-table">
                            <thead>
                                <tr>
                                    <th>Коли</th>
                                    <th>Хто</th>
                                    <th>Тип</th>
                                    <th>Товар</th>
                                    <th>Варіант</th>
                                    <th>Артикул</th>
                                    <th>Ціна</th>
                                    <th className="text-nowrap">Offer ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {changelogItems.map((c) => (
                                    <tr key={c.rowKey || c.logId}>
                                        <td className="text-nowrap">{formatSnapshotAt(c.at)}</td>
                                        <td>{c.userDisplayName || "—"}</td>
                                        <td>{c.kind === "bulk" ? "Масово (товар)" : "Один варіант"}</td>
                                        <td>{c.productName || "—"}</td>
                                        <td>{c.propsLabel || "—"}</td>
                                        <td>
                                            {c.sku ? <code>{c.sku}</code> : <span className="text-muted">—</span>}
                                        </td>
                                        <td className="text-nowrap">
                                            {c.oldPrice != null && Number.isFinite(Number(c.oldPrice)) ? (
                                                <span className="text-decoration-line-through text-muted me-1">
                                                    {Number(c.oldPrice)} грн
                                                </span>
                                            ) : null}
                                            <span className="fw-semibold">{c.newPrice} грн</span>
                                        </td>
                                        <td className="small text-muted">{c.offerId ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        </div>
                    )}
                    {!changelogLoading && changelogItems.length === 0 && (
                        <p className="text-muted mb-0">Записів поки немає.</p>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};
